import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  LucideAngularModule,
  ArrowRight,
  ArrowLeft,
  Eye,
  EyeOff,
  Mail,
  Lock,
  Star,
} from 'lucide-angular';
import { AuthService } from '../auth.service';
import { UserType } from '../auth.models';
import { ForgotPasswordModalComponent } from '../../components/forgot-password-modal/forgot-password-modal';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, LucideAngularModule, RouterLink, ForgotPasswordModalComponent],
  templateUrl: './login.html',
  styleUrl: './login.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  private readonly fb = inject(FormBuilder);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly isLoading$ = new BehaviorSubject<boolean>(false);
  protected readonly errorMessage$ = new BehaviorSubject<string>('');
  protected readonly showPassword = signal(false);
  protected readonly showForgotPasswordModal = signal(false);

  protected readonly loginForm: FormGroup;

  protected readonly icons = {
    ArrowRight,
    ArrowLeft,
    Eye,
    EyeOff,
    Mail,
    Lock,
    Star,
  };

  /** Redirect URL from query params (set by the caller, feature-agnostic) */
  protected readonly redirectUrl: string | null;

  /** Whether we're in a funnel mode (has a redirectUrl pointing to a feature) */
  protected readonly isFunnelMode: boolean;

  /** User type from query params (forwarded back to sign-up) */
  private readonly userType: UserType;

  /** Query params to forward when navigating between login and sign-up */
  protected readonly forwardQueryParams: Record<string, string>;

  constructor() {
    this.loginForm = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });

    const params = this.route.snapshot.queryParams;
    this.redirectUrl = params['redirectUrl'] || null;
    this.isFunnelMode = !!this.redirectUrl;
    this.userType = (params['userType'] as UserType) || 'teacher';

    // Build query params to forward when switching between login ↔ sign-up
    const forward: Record<string, string> = {};
    if (this.redirectUrl) {
      forward['redirectUrl'] = this.redirectUrl;
    }
    if (this.userType !== 'teacher') {
      forward['userType'] = this.userType;
    }
    this.forwardQueryParams = forward;
  }

  protected togglePasswordVisibility(): void {
    this.showPassword.update((v) => !v);
  }

  protected openForgotPasswordModal(): void {
    this.showForgotPasswordModal.set(true);
  }

  protected closeForgotPasswordModal(): void {
    this.showForgotPasswordModal.set(false);
  }

  protected goBack(): void {
    if (this.redirectUrl) {
      // Navigate back to the feature page (path only, without action query params)
      const path = this.redirectUrl.split('?')[0];
      this.router.navigateByUrl(path);
    } else {
      this.router.navigate(['/']);
    }
  }

  protected onSubmit(): void {
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      return;
    }

    this.isLoading$.next(true);
    this.errorMessage$.next('');

    const { email, password } = this.loginForm.value;
    const request = { email, password };

    this.authService
      .signIn(request, this.redirectUrl ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isLoading$.next(false);
          this.handleAuthSuccess();
        },
        error: (error) => {
          this.isLoading$.next(false);
          this.errorMessage$.next(error.error?.message || 'Invalid email or password. Please try again.');
        },
      });
  }

  protected signInWithGoogle(): void {
    this.authService.signInWithGoogle(this.redirectUrl ?? undefined);
  }

  protected signInWithMicrosoft(): void {
    this.authService.signInWithMicrosoft(this.redirectUrl ?? undefined);
  }

  private handleAuthSuccess(): void {
    // If a redirectUrl was provided, go there; otherwise let RoleRedirectComponent handle it
    this.router.navigateByUrl(this.redirectUrl ?? '/dashboard');
  }
}

